import * as React from "react";
import styled from "styled-components";

import {
  ActionBar,
  MobileNavButtonType,
  MobilePropertySelectorType,
  Premise
} from "@origin-digital/action-bar";
import { breakPoint } from "@origin-digital/helper";
import {
  AlertBanner,
  AlertStatusLabels,
  BREAK_POINTS,
  Colors,
  HeadingTagType,
  Text
} from "@origin-digital/style-guide";
import {
  eventDispatcher,
  IActionBarUpdate,
  IAlertBanner,
  IAlertBannerClose,
  IOriginEvents,
  ISubscription,
  navigationBack,
  navigationClose,
  TOPICS
} from "@origin-digital/event-dispatcher";

import { Z_INDEX } from "../../consts/style";
import { addMeshLinks } from "./helpers";
import { Hidden, Section } from "@origin-digital/ods-core";
const ACTION_BAR_HEIGHT = 136;

interface ActionBarProps {
  title?: string;
  mobileNavButton?: MobileNavButtonType;
  mobilePropertySelector?: MobilePropertySelectorType;
  component?: HeadingTagType;
  onSelectPremise?: (premiseId: string) => void;
  premiseId?: string;
  premises?: Premise[];
}

interface AlertBannerProps {
  message: string;
  heading?: string;
  type?: AlertStatusLabels;
  uniqueId?: string;
}

export interface HeaderProps extends ActionBarProps {
  authenticated?: boolean;
  hideActionBar?: boolean | "desktop";
}

interface HeaderState {
  actionBar: ActionBarProps;
  alertBanner?: AlertBannerProps;

  actionBarUpdateUnsubscribe?: ISubscription;
  alertBannerOpenUnsubscribe?: ISubscription;
  alertBannerCloseUnsubscribe?: ISubscription;
}

const HeaderContainer = styled.div`
  z-index: ${Z_INDEX.HEADER};
  position: sticky;
  top: 0;
  ${breakPoint("LG", BREAK_POINTS.Min.LG)`
    top: -${ACTION_BAR_HEIGHT}px;
    `};
`;

// TODO: move border to originate
const BottomLine = styled.div`
  border-bottom: 1px solid ${Colors.GREY16};
`;

export class Header extends React.Component<HeaderProps, HeaderState> {
  constructor(props: HeaderProps, state: HeaderState) {
    super(props, state);
    const { title, mobileNavButton, mobilePropertySelector, component } = props;
    this.state = {
      actionBar: { title, mobileNavButton, mobilePropertySelector, component }
    };
    this.handleOnCloseAlertBanner = this.handleOnCloseAlertBanner.bind(this);
  }

  componentDidMount() {
    const actionBarUpdateUnsubscribe = eventDispatcher.addListener(
      TOPICS.ACTION_BAR_UPDATE,
      (topic: IOriginEvents) => {
        if (topic.topic === TOPICS.ACTION_BAR_UPDATE) {
          this.handleActionBarUpdate(topic);
        }
      }
    );

    const alertBannerOpenUnsubscribe = eventDispatcher.addListener(
      TOPICS.ALERT_BANNER_OPEN,
      (topic: IOriginEvents) => {
        if (topic.topic === TOPICS.ALERT_BANNER_OPEN) {
          this.handleAlertBannerOpen(topic);
        }
      }
    );

    const alertBannerCloseUnsubscribe = eventDispatcher.addListener(
      TOPICS.ALERT_BANNER_CLOSE,
      (topic: IOriginEvents) => {
        if (topic.topic === TOPICS.ALERT_BANNER_CLOSE) {
          this.handleAlertBannerClose(topic);
        }
      }
    );

    this.setState({
      actionBarUpdateUnsubscribe,
      alertBannerOpenUnsubscribe,
      alertBannerCloseUnsubscribe
    });
  }

  componentWillUnmount() {
    this.state.actionBarUpdateUnsubscribe &&
      this.state.actionBarUpdateUnsubscribe();
    this.state.alertBannerOpenUnsubscribe &&
      this.state.alertBannerOpenUnsubscribe();
    this.state.alertBannerCloseUnsubscribe &&
      this.state.alertBannerCloseUnsubscribe();
  }

  handleActionBarUpdate(topic: IActionBarUpdate) {
    this.setState(prevState => ({
      actionBar: {
        ...prevState.actionBar,
        ...topic.payload
      }
    }));
  }

  handleAlertBannerOpen(topic: IAlertBanner) {
    this.setState({ alertBanner: { ...topic.payload } });
  }

  handleAlertBannerClose(topic: IAlertBannerClose) {
    if (
      this.state.alertBanner &&
      this.state.alertBanner.uniqueId === topic.payload.uniqueId
    ) {
      this.handleOnCloseAlertBanner();
    }
  }

  handleOnClickMobileNavButton() {
    if (this.state.actionBar.mobileNavButton === "close") {
      navigationClose();
    } else if (typeof window !== "undefined") {
      navigationBack();
    }
  }

  handleOnCloseAlertBanner() {
    this.setState({ alertBanner: undefined });
  }

  render() {
    const { hideActionBar, onSelectPremise, premiseId, premises } = this.props;
    const { actionBar, alertBanner } = this.state;
    const breakpointToHide = hideActionBar === "desktop" ? "md" : undefined;
    const showActionBar = hideActionBar === "desktop" ? true : !hideActionBar;

    return (
      <HeaderContainer>
        {showActionBar ? (
          <Hidden above={breakpointToHide}>
            <Section
              paddingY="none"
              hideGutter={[true, false]}
              fluidity={[true, false]}
            >
              <ActionBar
                mobilePropertySelector={actionBar.mobilePropertySelector}
                mobileNavButton={actionBar.mobileNavButton}
                headingComponent={actionBar.component}
                onClickMobileNavButton={(_e: any) =>
                  this.handleOnClickMobileNavButton()
                }
                title={actionBar.title}
                onSelectPremise={onSelectPremise}
                selectedPremiseId={premiseId}
                premises={premises}
              />
            </Section>
            <BottomLine />
          </Hidden>
        ) : null}
        {alertBanner ? (
          <AlertBanner
            heading={alertBanner.heading}
            type={alertBanner.type}
            onClose={this.handleOnCloseAlertBanner}
          >
            <Text markdown markdownLinkMutator={addMeshLinks}>
              {alertBanner.message}
            </Text>
          </AlertBanner>
        ) : null}
      </HeaderContainer>
    );
  }
}
